/* import __COLOCATED_TEMPLATE__ from './unified-content.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type { EntityType } from 'embercom/models/data/entity-types';

import KnowledgeHubApi from 'embercom/lib/knowledge-hub/list-api';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import type { SortDirection } from 'embercom/lib/knowledge-hub/constants';
import { FolderFilterOption } from 'embercom/lib/content-service/search-api';
import {
  ALL_CONTENT_COLUMN_DATA,
  KNOWLEDGE_HUB_REQUIRED_COLUMNS,
  DEFAULT_SELECTABLE_COLUMNS,
  type TableColumn,
  type ColumnData,
  IMPORT_SOURCE_IN_PROGRESS_STATUSES,
  IMPORT_SOURCE_STATUSES,
} from 'embercom/lib/knowledge-hub/constants';
import { RUN_STATUSES } from 'embercom/lib/external-content/constants';
import { type ContentCardType } from './content-cards';
import type UnifiedContentStateService from 'embercom/services/unified-content-state';
import {
  IMPORT_JOB_DELETE_STATUS_ID,
  IMPORT_JOB_STATUS_EVENT_ID,
  CONTENT_IMPORT_SOURCE_DELETION_STATUS_EVENT_ID,
  IMPORT_SERVICE_ROOT_FOLDER_CREATED_EVENT_ID,
  type UpdateImportSourceStatusEvent,
  type ImportServiceImportSourceDeletedEvent,
  BULK_ACTION_PROGRESSION_ID,
  type BulkActionProgressionEvent,
  BulkActionProgressionValues,
} from 'embercom/services/knowledge-hub-service';
import {
  type ContentImportRunEvent,
  EVENT_SERVICE_ID,
  FIN_INGESTION_POLLING_INTERVAL,
} from 'embercom/services/content-import-service';

import { getOwner } from '@ember/application';
import type Store from '@ember-data/store';
import { AIV3_CONTENT_INGESTION_JOB_STATUS_CHANGED_EVENT_ID } from '../content-setup-v2/filterable-list';
import type IntlService from 'ember-intl/services/intl';
import { timeout } from 'ember-concurrency';
import ENV from 'embercom/config/environment';
import type ContentImportSource from 'embercom/models/content-service/content-import-source';
import { saveAdvancedSettings } from 'embercom/utils/knowledge-hub/advanced-settings-helpers';
import type ModalService from 'embercom/services/modal-service';

// Grouped argument interfaces
export interface HeaderArgs {
  showSearch?: boolean;
  showGeneralFilters?: boolean;
  showAudienceFilter?: boolean;
  title?: string;
  showHamburgerMenu?: boolean;
  showActionButtons?: boolean;
  showPreview?: boolean;
  onToggleSidebar?: () => void;
  togglePreview?: () => void;
  onNewContent?: () => void;
  customSearchPlaceholder?: string;
  customContentTypes?: string[];
}

export interface ContentSourceArgs {
  showTotalCount?: boolean;
  defaultColumns?: string[];
  openSectionId?: string;
  setOpenSectionId?: (sectionId: string) => void;
  showContentCards?: boolean;
  showContentSources?: boolean;
  onContentModal?: (contentType: ContentCardType) => void;
  showModal?: (syncSourceType: string | undefined, entityType: EntityType) => Promise<void>;

  // Translation controls
  showAllTranslations?: boolean;
  toggleTranslations?: () => void;

  // Sorting controls
  sortBy?: string;
  setSortBy?: (sortBy?: string) => void;
  sortDirection?: SortDirection;
  setSortDirection?: (sortDirection?: string) => void;
}

interface Signature {
  Args: {
    showHeader?: boolean;
    finIsLive?: boolean;
    headerArgs?: HeaderArgs;
    contentSourceArgs?: ContentSourceArgs;
    nativeContent?: boolean;
    showSyncedSourceBulkRowActions?: boolean;
    // Routes for folder navigation. Passed down from route templates
    // to keep components decoupled from router. Defaults to Knowledge Hub if not provided.
    parentFolderRoute?: string;
    allContentRoute?: string;
    demoType?: string;
  };
}
export default class UnifiedContent extends Component<Signature> {
  @service declare appService: AppService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare unifiedContentState: UnifiedContentStateService;
  @service declare realTimeEventService: { on: Function; off: Function; subscribeTopics: Function };
  @service declare store: Store;
  @service declare notificationsService: any;
  @service declare intl: IntlService;
  @service declare modalService: ModalService;

  @tracked previewLocale = 'en';
  @tracked previewMode: 'agent' | 'copilot' = 'agent';
  @tracked columns: TableColumn[] = [];
  @tracked userModifiedColumnsInSearch = false;
  @tracked aiV3ContentIngestionReloadTimer: ReturnType<typeof setTimeout> | null = null;
  @tracked private localListApi?: KnowledgeHubApi;

  constructor(owner: unknown, args: any) {
    super(owner, args);

    this.columns = this.defaultColumns;
    this.previewLocale = this.appService.app.locale || 'en';
    if (this.appService.app.canUseFinPreviewAutoLanguageDetection) {
      this.previewLocale = 'auto';
    }
    this.unifiedContentState.resetFilters();
    this.initializeListApi();
    this.subscribeToImportEvents();
    this.subscribeToAiV3ContentIngestionEvents();
    this.subscribeToBulkActionEvents();
  }

  // Returns the current API instance.
  // Returns the API from knowledgeHubService if available

  get listApi(): KnowledgeHubApi | undefined {
    return this.knowledgeHubService.api || this.localListApi;
  }

  set listApi(value: KnowledgeHubApi | undefined) {
    this.localListApi = value;
  }

  willDestroy() {
    super.willDestroy();
    this.unsubscribeFromImportEvents();
    this.unsubscribeFromAiV3ContentIngestionEvents();
    this.unsubscribeFromBulkActionEvents();
    if (this.aiV3ContentIngestionReloadTimer) {
      clearTimeout(this.aiV3ContentIngestionReloadTimer);
      this.aiV3ContentIngestionReloadTimer = null;
    }
  }

  private async initializeListApi() {
    // Use the route's API if available, otherwise create our own
    if (this.knowledgeHubService.api) {
      this.listApi = this.knowledgeHubService.api;
      this.unifiedContentState.registerListApi(this.listApi);
    } else {
      // Ensure folders are loaded into Ember Data store so content wrappers can resolve their parent folder relationships for proper tree structure rendering.
      await this.knowledgeHubService.fetchFoldersOnce();

      let defaultSearchParams = {
        app_id: this.appService.app.id as string,
        content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
        folder_filter_option: FolderFilterOption.None,
        object_types: this.knowledgeHubService.getSearchObjectTypes(),
        sort_by: 'title',
        sort_direction: 'asc' as SortDirection,
        native_content: this.args.nativeContent ?? false,
      };

      this.listApi = new KnowledgeHubApi(getOwner(this), defaultSearchParams);
      this.unifiedContentState.registerListApi(this.listApi);
      await this.listApi.loadPage();
    }
  }

  @action
  setPreviewLocale(locale: string) {
    this.previewLocale = locale;
  }

  @action
  setPreviewMode(mode: 'agent' | 'copilot') {
    this.previewMode = mode;
  }

  noop() {
    // No-op function for optional callbacks
  }

  resetFilters = () => {
    this.unifiedContentState.resetFilters();
    this.userModifiedColumnsInSearch = false;
    this.listApi?.reset();
    this.listApi?.loadPage();
  };

  get defaultColumns() {
    let spacerColumn: TableColumn = { valuePath: 'spacer' };
    // Fin/Content shows only Status & AI Agent Columns
    // KH shows Status, Help Center, Copilot, AI Agent Columns.
    // Based on route, if null uses defualt of KH
    let columns = [
      ...KNOWLEDGE_HUB_REQUIRED_COLUMNS,
      ALL_CONTENT_COLUMN_DATA.contentStatus,
      ...(!this.args.parentFolderRoute
        ? [ALL_CONTENT_COLUMN_DATA.helpCenterState, ALL_CONTENT_COLUMN_DATA.copilotState]
        : []),
      ALL_CONTENT_COLUMN_DATA.chatbotState,
    ];

    if (this.appService.app.canUseFinSalesAgent) {
      columns.push(ALL_CONTENT_COLUMN_DATA.salesAgentState);
    }

    if (this.appService.app.canUseFinEcommerceAgent) {
      columns.push(ALL_CONTENT_COLUMN_DATA.ecommerceState);
    }

    columns.push(spacerColumn);
    return columns;
  }

  get showEmptyState(): boolean {
    return !this.listApi?.isLoadingPage && this.listApi?.contentWrappers.length === 0;
  }

  get hasAppliedFilter(): boolean {
    return this.unifiedContentState.hasAppliedFilter;
  }

  get hasActiveSearch(): boolean {
    return this.unifiedContentState.hasActiveSearch;
  }

  get displayColumns(): TableColumn[] {
    if (this.hasActiveSearch) {
      let spacerColumn: TableColumn = { valuePath: 'spacer' };
      let hasContentType = this.columns.some(
        (column) => column.valuePath === ALL_CONTENT_COLUMN_DATA.contentType.valuePath,
      );
      let otherColumns = this.columns.filter(
        (column) =>
          column.valuePath !== ALL_CONTENT_COLUMN_DATA.contentType.valuePath &&
          column.valuePath !== 'spacer',
      );
      // Show contentType if user hasn't modified columns yet OR if user explicitly selected it
      let shouldShowContentType = !this.userModifiedColumnsInSearch || hasContentType;
      if (shouldShowContentType) {
        let titleIndex = otherColumns.findIndex(
          (column) => column.valuePath === ALL_CONTENT_COLUMN_DATA.title.valuePath,
        );
        let insertIndex = titleIndex !== -1 ? titleIndex + 1 : 0;
        return [
          ...otherColumns.slice(0, insertIndex),
          ALL_CONTENT_COLUMN_DATA.contentType,
          ...otherColumns.slice(insertIndex),
          spacerColumn,
        ];
      }
      return [...otherColumns, spacerColumn];
    }
    return this.columns;
  }

  get selectableColumns(): ColumnData[] {
    return DEFAULT_SELECTABLE_COLUMNS;
  }

  get showContentCards(): boolean {
    return !!this.args?.contentSourceArgs?.showContentCards && !this.hasActiveSearch;
  }

  get shouldShowArticlesFolder(): boolean {
    return !this.args.nativeContent && !this.hasActiveSearch;
  }

  @action
  updatedSelectedTableColumns(columns: string[]) {
    let spacerColumn: TableColumn = { valuePath: 'spacer' };
    let selectedColumns = columns.map((col) => ALL_CONTENT_COLUMN_DATA[col]);
    this.columns = KNOWLEDGE_HUB_REQUIRED_COLUMNS.concat(selectedColumns, spacerColumn);
    // Track that user has modified columns during search
    if (this.hasActiveSearch) {
      this.userModifiedColumnsInSearch = true;
    }
  }

  @action
  async openAdvancedSettingsSheet(contentImportSource: ContentImportSource) {
    this.modalService.openModal(
      'react/knowledge-hub/advanced-settings-websync/advanced-settings-websync-sheet',
      {
        appId: this.appService.app.id,
        adminId: this.appService.app.currentAdmin.id,
        contentImportSource,
        onSave: async (formData: Record<string, unknown>) => {
          await this.saveAdvancedSettings(contentImportSource, formData, false);
        },
        onSaveAndSync: async (formData: Record<string, unknown>) => {
          await this.saveAdvancedSettings(contentImportSource, formData, true);
        },
      },
    );
  }

  private async saveAdvancedSettings(
    contentImportSource: ContentImportSource,
    formData: Record<string, unknown>,
    shouldResync: boolean,
  ) {
    await saveAdvancedSettings({
      contentImportSource,
      formData,
      shouldResync,
      intl: this.intl,
      notificationsService: this.notificationsService,
      onRefresh: async () => {
        await this.refetchListApi();
      },
    });
  }

  private subscribeToImportEvents() {
    this.realTimeEventService.on(IMPORT_JOB_STATUS_EVENT_ID, this, 'handleImportStatusChange');
    this.realTimeEventService.on(IMPORT_JOB_DELETE_STATUS_ID, this, 'handleImportSourceDeleted');
    this.realTimeEventService.on(EVENT_SERVICE_ID, this, 'handleContentImportRunStatus');
    this.realTimeEventService.on(
      CONTENT_IMPORT_SOURCE_DELETION_STATUS_EVENT_ID,
      this,
      'refetchListApi',
    );
    this.realTimeEventService.on(
      IMPORT_SERVICE_ROOT_FOLDER_CREATED_EVENT_ID,
      this,
      'handleImportServiceRootFolderCreated',
    );
  }
  private unsubscribeFromImportEvents() {
    this.realTimeEventService.off(IMPORT_JOB_STATUS_EVENT_ID, this, 'handleImportStatusChange');
    this.realTimeEventService.off(IMPORT_JOB_DELETE_STATUS_ID, this, 'handleImportSourceDeleted');
    this.realTimeEventService.off(EVENT_SERVICE_ID, this, 'refetchListApi');
    this.realTimeEventService.off(
      IMPORT_SERVICE_ROOT_FOLDER_CREATED_EVENT_ID,
      this,
      'handleImportServiceRootFolderCreated',
    );
  }

  private subscribeToAiV3ContentIngestionEvents() {
    this.realTimeEventService.on(
      AIV3_CONTENT_INGESTION_JOB_STATUS_CHANGED_EVENT_ID,
      this,
      'handleAiV3ContentIngestionJobStatusChange',
    );
  }

  private unsubscribeFromAiV3ContentIngestionEvents() {
    this.realTimeEventService.off(
      AIV3_CONTENT_INGESTION_JOB_STATUS_CHANGED_EVENT_ID,
      this,
      'handleAiV3ContentIngestionJobStatusChange',
    );
  }

  private subscribeToBulkActionEvents() {
    this.realTimeEventService.on(
      BULK_ACTION_PROGRESSION_ID,
      this,
      'handleBulkActionProgressStatus',
    );
  }

  private unsubscribeFromBulkActionEvents() {
    this.realTimeEventService.off(
      BULK_ACTION_PROGRESSION_ID,
      this,
      'handleBulkActionProgressStatus',
    );
  }

  // event for sources like zendesk, salesorce, notion, confluence, etc.
  async handleImportStatusChange(event: UpdateImportSourceStatusEvent) {
    let source = this.store.peekRecord('import-service/import-source', event.import_source_id);
    if (!source) {
      return;
    }

    let previousStatus = source.status;
    source.status = event.status;

    if (this.shouldRefreshOnStatusChange(event.status, previousStatus)) {
      await this.reloadImportData();
    }
  }

  async handleImportSourceDeleted(event: ImportServiceImportSourceDeletedEvent) {
    if (event.is_deleted) {
      await this.reloadImportData();
    }
  }

  // event for website import.
  async handleContentImportRunStatus(event: ContentImportRunEvent) {
    let isCompleted =
      event?.event_type === RUN_STATUSES.completed || event?.event_type === RUN_STATUSES.failed;
    if (isCompleted) {
      await this.reloadImportData();
    }
  }

  private shouldRefreshOnStatusChange(currentStatus: string, previousStatus: string): boolean {
    let isStarting =
      IMPORT_SOURCE_IN_PROGRESS_STATUSES.includes(currentStatus) &&
      !IMPORT_SOURCE_IN_PROGRESS_STATUSES.includes(previousStatus);
    let isComplete = currentStatus === IMPORT_SOURCE_STATUSES.success;

    return isStarting || isComplete;
  }

  async handleImportServiceRootFolderCreated() {
    await timeout(ENV.APP._1000MS); // Give ES time to update before we reload the list
    await this.reloadImportData();
  }

  private async reloadImportData(): Promise<void> {
    await this.store.findAll('import-service/import-source', { reload: true });
    await this.knowledgeHubService.fetchFoldersOnce(true);
    await this.listApi?.search();
    this.knowledgeHubService.fetchKnowledgeUsageSummary();
  }

  async refetchListApi(): Promise<void> {
    await this.listApi?.search();
  }

  async handleBulkActionProgressStatus(event: BulkActionProgressionEvent) {
    let currentAdmin = this.appService.app.currentAdmin;
    if (currentAdmin.id !== event.admin_id.toString()) {
      return;
    }

    if (event.status === BulkActionProgressionValues.Complete) {
      if (event.all_entities_unaffected) {
        this.knowledgeHubService.notifyNoEntitiesAffected(
          event.bulk_action,
          this.appService.app.canUseStandalone,
        );
      } else {
        await timeout(ENV.APP._1000MS); // Give ES time to update before we reload the list
        this.knowledgeHubService.fetchKnowledgeUsageSummary();
        this.sendCompletionNotification(event);
        this.refetchListApi();
      }
    }

    if (event.status === BulkActionProgressionValues.InvalidFolder) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.filterable-list.bulk-actions.invalid-folder'),
      );
    }
  }

  private async sendCompletionNotification(event: BulkActionProgressionEvent) {
    let action = event.bulk_action;
    let unaffectedEntityTypes = event.unaffected_entity_types;
    let language_code = event.language_code;
    let destinationFolderId = event.destination_folder_id;
    this.knowledgeHubService.notifyBulkActionCompleted(
      action,
      unaffectedEntityTypes,
      language_code,
      destinationFolderId,
      this.appService.app.canUseStandalone,
    );
    if (destinationFolderId) {
      await this.knowledgeHubService.fetchFoldersOnce(true);
    }
  }

  // @ts-ignore - Used as event handler callback in subscribeToAiV3ContentIngestionEvents
  private handleAiV3ContentIngestionJobStatusChange(event: any) {
    if (this.aiV3ContentIngestionReloadTimer) {
      clearTimeout(this.aiV3ContentIngestionReloadTimer);
    }
    this.performDelayedReload();
  }

  private performDelayedReload() {
    this.aiV3ContentIngestionReloadTimer = setTimeout(() => {
      this.reloadImportData();
      this.aiV3ContentIngestionReloadTimer = null;
    }, FIN_INGESTION_POLLING_INTERVAL);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::UnifiedContent::UnifiedContent': typeof UnifiedContent;
    'knowledge-hub/unified-content/unified-content': typeof UnifiedContent;
  }
}
